/**
 * HomeDashboardV2 — "Home" screen matching the approved mockup (hero banner excluded).
 * Layout/visuals follow the mockup; all numbers and rows are sourced from existing
 * endpoints (agentRunAPI, taskService, useRun, onboarding domains) — no fabricated data.
 * Where the mockup implies data we don't track (per-run completion %), we show an
 * honest indeterminate bar plus elapsed time instead of inventing a number.
 *
 * The composer hands its prompt to the full AI Assistant page (/ai-assistant), which
 * owns execution (mentions, tools, files, model choice, history).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Zap, Bell, CheckCircle2, CalendarClock, Key, X, ChevronRight, ChevronDown, MoreVertical,
  Mic, Send, Cpu, BookOpen, Users, Code2, BarChart3, FileText, Lightbulb, Sparkles, ListChecks, Briefcase,
} from 'lucide-react';
import AppShellV2, { T2 } from './AppShellV2.tsx';
import { getCurrentUserProfile } from '../../utils/auth.ts';
import { agentRunAPI, onboardingAPI, externalAgentsAPI, agenticFinanceAPI } from '../../services/api.js';
import { taskService } from '../../services/taskApi.ts';
import { useRun } from '../../context/RunContext.tsx';
import { useAI } from '../../context/AIContext.jsx';
import UpgradeModal from '../modals/UpgradeModal.tsx';
import ExternalAgentsPanel from './ExternalAgentsPanel.tsx';
import HomeComposer from './HomeComposer.tsx';
import HomeAiTeamPanel from './HomeAiTeamPanel.tsx';
import WorkforceConnect from './WorkforceConnect.tsx';
import { useDomainAgentSelection } from '../../hooks/useDomainAgentSelection.ts';
import { connectorsAPI } from '../../services/api.js';
import { warmKnowledgeService } from '../../pages/VaultPage.tsx';

type IconType = React.ComponentType<{ size?: number; color?: string }>;

const GREEN = 'var(--lyra-color-primary)';

function elapsedLabel(createdAt: string) {
  const s = Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function timeAgo(dateStr: string) {
  const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const timeOf = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{ background: T2.surface, border: `1px solid ${T2.border}`, borderRadius: 18, ...style }}>{children}</div>
);

const LinkBtn: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button onClick={onClick} style={{ border: 'none', background: 'none', color: T2.accentText, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, whiteSpace: 'nowrap' }}>
    {children} →
  </button>
);

const IconTile: React.FC<{ Icon: IconType; bg: string; fg: string; size?: number }> = ({ Icon, bg, fg, size = 44 }) => (
  <div style={{ width: size, height: size, borderRadius: 12, background: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
    <Icon size={Math.round(size * 0.46)} />
  </div>
);

// Suggestion cards — each is gated on the onboarding domain whose tools it needs.
const CARD_POOL: Array<{ domain: string; title: string; desc: string; prompt: string; Icon: IconType; bg: string; fg: string }> = [
  { domain: 'hr',     title: 'Plan hiring needs',   desc: 'Check internal capacity and suggest open roles', prompt: 'Plan our hiring needs: check internal capacity and suggest open roles', Icon: Users,     bg: 'var(--app-surface-3)', fg: 'var(--lyra-color-success)' },
  { domain: 'coding', title: 'Review a repository', desc: 'Analyze code, find issues and suggest fixes',     prompt: 'Review a repository: analyze the code, find issues and suggest fixes',   Icon: Code2,     bg: 'var(--app-primary-soft)', fg: 'var(--lyra-color-primary)' },
  { domain: 'hr',     title: 'Project demand',      desc: 'Summarize client needs and resource requirements', prompt: 'Show project demand: summarize client needs and resource requirements',  Icon: BarChart3, bg: 'var(--app-surface-3)', fg: '#5B4BDB' },
  { domain: 'hr',     title: 'Workforce briefing',  desc: 'Get a summary of your team and projects',         prompt: 'Give me a workforce briefing: a summary of my team and projects',        Icon: FileText,  bg: 'var(--app-primary-soft)', fg: 'var(--lyra-color-primary)' },
  { domain: '*',      title: 'Plan my day',         desc: 'Turn tasks and calendar into a focused plan',     prompt: 'Plan my day',                                                           Icon: CalendarClock, bg: 'var(--lyra-color-surface-2)', fg: '#C2620C' },
  { domain: '*',      title: 'Summarize my emails', desc: 'Catch up on what needs a reply',                  prompt: 'Summarize my emails',                                                   Icon: Sparkles,  bg: 'var(--lyra-color-surface-2)', fg: '#0F766E' },
];

const QUICK_ACTIONS: Array<{ label: string; prompt: string; Icon: IconType; domain: string }> = [
  { label: 'List open roles',     prompt: 'List open roles',                domain: 'hr', Icon: Users },
  { label: 'Check team capacity', prompt: 'Check team capacity',            domain: 'hr', Icon: BarChart3 },
  { label: 'Project demand',      prompt: 'Show project demand',            domain: 'hr', Icon: FileText },
  { label: 'Workforce briefing',  prompt: 'Give me a workforce briefing',   domain: 'hr', Icon: ListChecks },
  { label: 'Plan my day',         prompt: 'Plan my day',                    domain: '*',  Icon: CalendarClock },
  { label: 'Summarize my emails', prompt: 'Summarize my emails',            domain: '*',  Icon: FileText },
  { label: 'Review open PRs',     prompt: 'Review open PRs',                domain: 'coding', Icon: Code2 },
];

const pickFor = <T extends { domain: string }>(pool: T[], domains: string[], max: number) => {
  const enabled = pool.filter((p) => p.domain !== '*' && domains.includes(p.domain));
  const general = pool.filter((p) => p.domain === '*');
  // Before domains load (or with none), show the workforce/engineering defaults like the mockup.
  const base = domains.length ? enabled : pool.filter((p) => p.domain !== '*');
  return [...base, ...general].slice(0, max);
};

type WorkRow = { id: string; name: string; sub: string; status: 'running' | 'completed' | 'queued'; right?: string; Icon: IconType; bg: string; fg: string };

const pillStyle = (status: WorkRow['status']): React.CSSProperties => {
  const map = { running: ['var(--app-surface-3)', 'var(--lyra-color-success)'], completed: ['var(--app-surface-3)', 'var(--lyra-color-primary)'], queued: ['var(--app-primary-soft)', 'var(--lyra-color-primary)'] } as const;
  const [bg, fg] = map[status];
  return { display: 'inline-flex', alignItems: 'center', gap: 5, background: bg, color: fg, borderRadius: 999, padding: '5px 12px', fontSize: 12.5, fontWeight: 600 };
};

export default function HomeDashboardV2({ onLogout }: { onLogout?: () => void }) {
  const navigate = useNavigate();
  const profile = useMemo(() => getCurrentUserProfile(), []);
  const firstName = profile?.name?.split(' ')[0] || 'there';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';

  const { activeRuns } = useRun();
  const { customApiKeys, getSelectedModel, selectedMode } = useAI();

  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 1180);
  useEffect(() => {
    const onResize = () => { setIsMobile(window.innerWidth <= 768); setIsNarrow(window.innerWidth <= 1180); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [scheduledRuns, setScheduledRuns] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [completedRuns, setCompletedRuns] = useState<any[]>([]);
  const [runStats, setRunStats] = useState<{ completedToday: number; failedToday: number }>({ completedToday: 0, failedToday: 0 });

  // ── BYOK banner — shown until the user has saved at least one verified key.
  // Dismiss is session-scoped so it returns if there is still no working key.
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return sessionStorage.getItem('lyra_apikey_banner_dismissed') === '1'; } catch { return false; }
  });
  const hasApiKey = useMemo(
    () => Object.values(customApiKeys || {}).some((k) => typeof k === 'string' && k.trim().length > 0),
    [customApiKeys]
  );
  const dismissBanner = () => {
    setBannerDismissed(true);
    try { sessionStorage.setItem('lyra_apikey_banner_dismissed', '1'); } catch {}
  };
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const [insightDismissed, setInsightDismissed] = useState(false);

  const load = useCallback(async () => {
    const results = await Promise.allSettled([
      agentRunAPI.getScheduled(),
      taskService.getCurrentWeekTasks?.(),
      agentRunAPI.listAll({ status: 'success', limit: 300 }),
      agentRunAPI.listAll({ limit: 1 }),
    ]);
    if (results[0].status === 'fulfilled') {
      const d: any = results[0].value;
      setScheduledRuns(Array.isArray(d?.runs) ? d.runs : []);
    }
    if (results[1].status === 'fulfilled') {
      const d: any = results[1].value;
      setTasks(Array.isArray(d) ? d : d?.tasks || []);
    }
    if (results[2].status === 'fulfilled') {
      const d: any = results[2].value;
      const runs = (Array.isArray(d?.runs) ? d.runs : [])
        .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setCompletedRuns(runs);
    }
    if (results[3].status === 'fulfilled') {
      const d: any = results[3].value;
      setRunStats({ completedToday: d?.stats?.completedToday || 0, failedToday: d?.stats?.failedToday || 0 });
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  // The knowledge service may be asleep; waking it now means the Vault is ready by the time it is opened.
  useEffect(() => { warmKnowledgeService(); }, []);
  // A chat run (e.g. "create a task for 8pm") may have changed tasks or schedules: reload when one ends.
  useEffect(() => {
    const onRunEnd = () => { load(); };
    window.addEventListener('lyra:run-end', onRunEnd);
    return () => window.removeEventListener('lyra:run-end', onRunEnd);
  }, [load]);

  // Domains the user checked in onboarding drive the capability chips and suggestions.
  const [domains, setDomains] = useState<string[]>([]);
  // Connected capabilities: the domain agents (MCP servers) the user connected, and the panel to connect or disconnect them.
  const selection = useDomainAgentSelection(null);
  const [capabilitiesOpen, setCapabilitiesOpen] = useState(false);
  const [workforceOpen, setWorkforceOpen] = useState(false);
  const [externalAgents, setExternalAgents] = useState<any[]>([]);
  const [selectedExternalAgentIds, setSelectedExternalAgentIds] = useState<string[]>([]);
  const loadExternalAgents = useCallback(() => externalAgentsAPI.list().then((r: any) => {
    const healthy = (r.agents || []).filter((a: any) => a.enabled && a.health?.status === 'healthy');
    setExternalAgents(healthy);
    setSelectedExternalAgentIds((chosen) => chosen.filter((id) => healthy.some((a: any) => a._id === id)));
  }).catch(() => setExternalAgents([])), []);
  useEffect(() => { loadExternalAgents(); }, [loadExternalAgents, capabilitiesOpen]);
  // Connections complement the workspace capabilities in the Home AI team panel.
  const [offered, setOffered] = useState<{ id?: string; label?: string; domain: string; connected: boolean; auth?: string }[]>([]);
  const loadOffered = useCallback(() => { connectorsAPI.list().then((r: { connectors: { id?: string; label?: string; domain: string; connected: boolean; auth?: string }[] }) => setOffered(r.connectors || [])).catch(() => {}); }, []);
  useEffect(() => { loadOffered(); }, [loadOffered, capabilitiesOpen]);
  const capabilityDomains = Array.from(new Set([...domains, ...offered.map((c) => c.domain)]));
  const startConnection = useCallback(async (connector: { id?: string; domain: string }) => {
    // Workforce has an intentionally richer setup flow: it either redirects
    // through OAuth or lets the user select/create their organization. Do not
    // send it through generic domain preferences.
    if (connector.domain === 'hr') { setWorkforceOpen(true); return; }
    if (!connector.id) return;
    try {
      const result: any = await connectorsAPI.connect(connector.id);
      if (result?.authorizeUrl) { window.location.assign(result.authorizeUrl); return; }
      await Promise.all([loadOffered(), selection.reload()]);
      // Provider connections refresh their agent registry in place.
    } catch { /* The provider's OAuth or organization flow owns the error state. */ }
  }, [loadOffered, selection.reload]);
  const disconnectConnection = useCallback(async (connector: { id?: string; domain: string }) => {
    // Workforce retains its own safe unlink behavior because it can be backed
    // by either an OAuth connection or an organization link.
    if (connector.domain === 'hr') { setWorkforceOpen(true); return; }
    try {
      // Finance is organization-backed, not an OAuth connector. It owns its
      // link and MCP server, so disconnect through its explicit endpoint.
      if (connector.domain === 'finance' && !connector.id) await agenticFinanceAPI.unlink();
      else if (connector.id) await connectorsAPI.disconnect(connector.id);
      else return;
      await Promise.all([loadOffered(), selection.reload()]);
    } catch {
      // Keep the card stable; the provider-specific panel can surface errors
      // on the next connection attempt.
    }
  }, [loadOffered, selection.reload]);
  // Back from an agent's approval page: a failure opens the panel (which shows it); a success just tidies the address bar.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connectError')) { setCapabilitiesOpen(true); return; }
    if (params.get('connected')) {
      params.delete('connected');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
  }, []);
  useEffect(() => {
    let cancelled = false;
    onboardingAPI.get()
      .then((res: any) => {
        const ds = res?.onboarding?.domains;
        if (!cancelled) setDomains(Array.isArray(ds) ? [...new Set<string>(ds.map((d: any) => String(d).toLowerCase()))] : []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const overdueTasks = tasks.filter((t: any) => t.dueDate && new Date(t.dueDate).getTime() < Date.now() && t.status !== 'Done' && !t.completed);
  const scheduledToday = scheduledRuns.filter((r: any) => isSameDay(new Date(r.scheduledAt), new Date()));
  // A task belongs to today when it is due today, or (no due date) it is planned for today's weekday in this week's plan.
  const todayDayNumber = new Date().getDay() || 7;
  const tasksDueToday = tasks.filter((t: any) => (t.dueDate ? isSameDay(new Date(t.dueDate), new Date()) : t.day === todayDayNumber));
  // A due date carrying a time of day (not midnight) is shown on the timeline at that time.
  const dueTime = (t: any): string | undefined => {
    if (!t.dueDate) return undefined;
    const d = new Date(t.dueDate);
    return d.getUTCHours() || d.getUTCMinutes() || d.getHours() || d.getMinutes() ? (t.dueDate as string) : undefined;
  };

  // Agenda for today: timed agent/workflow runs plus all-day task due-dates.
  const todayScheduleItems = [
    ...tasksDueToday.map((t: any) => ({ id: t._id, type: 'task' as const, name: t.title || 'Task', status: t.status, sortTime: dueTime(t) ? new Date(t.dueDate).getTime() : 0, time: dueTime(t) })),
    ...scheduledToday
      .map((r: any) => ({ id: r._id, type: (r.chainId ? 'workflow' : 'agent') as 'workflow' | 'agent', name: r.agentName || 'Scheduled run', status: 'scheduled', sortTime: new Date(r.scheduledAt).getTime(), time: r.scheduledAt as string | undefined })),
  ].sort((a: any, b: any) => a.sortTime - b.sortTime);

  const attentionItems: Array<{ id: string; title: string; subtitle: string; link: string }> = [];
  if (overdueTasks.length > 0) attentionItems.push({ id: 'overdue', title: `${overdueTasks.length} task${overdueTasks.length > 1 ? 's' : ''} overdue`, subtitle: `Due ${timeAgo(overdueTasks[0].dueDate)}`, link: '/tools/project-management/personal-kanban' });
  if (runStats.failedToday > 0) attentionItems.push({ id: 'failed', title: `${runStats.failedToday} run${runStats.failedToday > 1 ? 's' : ''} failed today`, subtitle: 'Mission Control', link: '/mission-control?filter=failed&since=today' });
  if (scheduledToday.length > 0) attentionItems.push({ id: 'sched', title: `${scheduledToday.length} run${scheduledToday.length > 1 ? 's' : ''} scheduled today`, subtitle: 'Confirm upcoming automation', link: '/schedule' });

  // ── Composer ────────────────────────────────────────────────────────────
  const [prompt, setPrompt] = useState('');
  const [listening, setListening] = useState(false);
  const recogRef = useRef<any>(null);
  // Quick actions run in the Home composer itself (the same event New Task uses); nothing navigates away.
  const goChat = (text: string, autoSend: boolean) => {
    if (!text.trim()) return;
    window.dispatchEvent(new CustomEvent('lyra:set-run-target', { detail: { prepopulateText: text.trim(), autoSend } }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const send = () => { if (prompt.trim()) goChat(prompt, true); };

  const SpeechRec = typeof window !== 'undefined' ? ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition) : null;
  const toggleMic = () => {
    if (!SpeechRec) return;
    if (listening) { recogRef.current?.stop(); return; }
    const rec = new SpeechRec();
    rec.interimResults = false;
    rec.onresult = (e: any) => setPrompt((p) => `${p ? p + ' ' : ''}${e.results[0][0].transcript}`);
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recogRef.current = rec;
    setListening(true);
    rec.start();
  };

  const selectedModel = getSelectedModel ? getSelectedModel() : null;
  const modelLabel = selectedMode === 'manual' && selectedModel ? (selectedModel.providerLabel || selectedModel.name) : 'Auto';

  const toolbarBtn: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 8, height: 46, padding: '0 18px', borderRadius: 12,
    border: `1px solid ${T2.border}`, background: 'var(--lyra-color-surface)', color: T2.ink, fontSize: 13.5, fontWeight: 500, cursor: 'pointer',
  };

  // ── Your work ───────────────────────────────────────────────────────────
  const [workTab, setWorkTab] = useState<'running' | 'completed' | 'queued'>('running');
  const workRows: Record<'running' | 'completed' | 'queued', WorkRow[]> = {
    running: activeRuns.slice(0, 4).map((r) => ({
      id: r._id, name: r.agentName || 'Agent', sub: r.input || 'Working…', status: 'running', right: elapsedLabel(r.createdAt),
      Icon: r.chainId ? Briefcase : Users, bg: 'var(--app-primary-soft)', fg: 'var(--lyra-color-primary)',
    })),
    completed: completedRuns.slice(0, 4).map((r: any) => ({
      id: r._id, name: r.agentName || 'Agent run', sub: r.input || 'Completed', status: 'completed', right: timeAgo(r.createdAt),
      Icon: CheckCircle2, bg: 'var(--app-surface-3)', fg: 'var(--lyra-color-primary)',
    })),
    queued: scheduledRuns
      .filter((r: any) => new Date(r.scheduledAt).getTime() >= Date.now())
      .sort((a: any, b: any) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime())
      .slice(0, 4)
      .map((r: any) => ({
        id: r._id, name: r.agentName || 'Scheduled run', sub: r.input || 'Scheduled', status: 'queued' as const,
        right: `${isSameDay(new Date(r.scheduledAt), new Date()) ? 'Today' : new Date(r.scheduledAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${timeOf(r.scheduledAt)}`,
        Icon: CalendarClock, bg: 'var(--app-primary-soft)', fg: 'var(--lyra-color-primary)',
      })),
  };
  const emptyText = { running: 'Nothing running right now.', completed: 'No completed runs yet.', queued: 'Nothing queued.' };

  // ── Insight (derived from real data only) ───────────────────────────────
  const insight = overdueTasks.length > 0
    ? { text: `You have ${overdueTasks.length} overdue task${overdueTasks.length > 1 ? 's' : ''} — your agents can help clear them.`, link: '/tools/project-management/personal-kanban' }
    : runStats.failedToday > 0
      ? { text: `${runStats.failedToday} run${runStats.failedToday > 1 ? 's' : ''} failed today. Review them in Mission Control.`, link: '/mission-control?filter=failed&since=today' }
      : completedRuns.length > 0
        ? { text: `Your agents have completed ${completedRuns.filter((r: any) => Date.now() - new Date(r.createdAt).getTime() < 7 * 864e5).length} runs this week.`, link: '/mission-control' }
        : null;

  const stats: Array<{ value: number; label: string; Icon: IconType; bg: string; fg: string; link: string }> = [
    { value: activeRuns.length, label: 'Running', Icon: Zap, bg: 'var(--app-surface-3)', fg: 'var(--lyra-color-success)', link: '/mission-control?filter=running' },
    { value: attentionItems.length, label: 'Need attention', Icon: Bell, bg: 'var(--lyra-color-danger-bg)', fg: 'var(--lyra-color-danger)', link: '/mission-control' },
    { value: runStats.completedToday, label: 'Completed', Icon: CheckCircle2, bg: 'var(--app-surface-3)', fg: 'var(--lyra-color-primary)', link: '/mission-control' },
    { value: todayScheduleItems.length, label: 'Scheduled', Icon: CalendarClock, bg: 'var(--app-primary-soft)', fg: 'var(--lyra-color-primary)', link: '/schedule' },
  ];

  const quick = pickFor(QUICK_ACTIONS, domains, 4);

  return (
    <AppShellV2 onLogout={onLogout}>
      <div style={{ maxWidth: 1360, margin: '0 auto', padding: isMobile ? '16px 16px 32px' : '30px 36px 36px' }}>
        {/* BYOK API key banner — dismissible, hidden once a key exists */}
        {!hasApiKey && !bannerDismissed && (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 14, background: T2.accentSoft, border: `1px solid ${T2.border}`, borderRadius: 14, padding: '12px 16px', marginBottom: 20 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--lyra-color-surface)', color: T2.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Key size={16} /></div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: T2.ink }}>Connect your API key to unlock the full power of Lyra.</div>
              <div style={{ fontSize: 12, color: T2.ink3, marginTop: 2 }}>Use your own API keys (BYOK) for OpenAI, Anthropic, Google and more.</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
              <button onClick={() => navigate('/ai-console')} style={{ border: 'none', background: T2.accent, color: 'var(--app-on-accent)', borderRadius: 9, padding: '9px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>Add API Key</button>
              <button onClick={() => setShowUpgradeModal(true)} style={{ border: `1px solid ${T2.border}`, background: 'var(--lyra-color-surface)', color: T2.ink, borderRadius: 9, padding: '8px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>Upgrade to Pro</button>
              <button onClick={dismissBanner} title="Dismiss" style={{ border: 'none', background: 'none', color: T2.ink3, cursor: 'pointer', padding: 4, display: 'flex' }}><X size={16} /></button>
            </div>
          </div>
        )}

        {/* Greeting */}
        <div style={{ marginBottom: 26 }}>
          <div style={{ fontSize: isMobile ? 22 : 24, fontWeight: 700, color: T2.ink, letterSpacing: '-0.02em' }}>Good {greeting}, {firstName} <span role="img" aria-label="wave">👋</span></div>
          <div style={{ fontSize: 14, color: T2.ink3, marginTop: 4 }}>What would you like your AI team to work on today?</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? 'minmax(0,1fr)' : 'minmax(0,1fr) 340px', gap: 20, alignItems: 'start' }}>
          {/* ════ Main column ════ */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
            {/* Composer: the real chat, answers appear here */}
            <HomeComposer selection={selection} externalAgentIds={selectedExternalAgentIds} />

            {/* The operational overview belongs below the chat, leaving the
                right rail focused entirely on the user's AI team. */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : 'repeat(3, minmax(0,1fr))', gap: 14 }}>
              <Card style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 7 }}><div style={{ fontSize: 14, fontWeight: 700, color: T2.ink }}>Today</div><div style={{ fontSize: 11.5, color: T2.ink3 }}>{new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</div></div>
                {stats.map((s, i) => <button key={s.label} onClick={() => navigate(s.link)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', border: 'none', borderTop: i ? `1px solid ${T2.border}` : 'none', background: 'none', cursor: 'pointer', textAlign: 'left' }}><IconTile Icon={s.Icon} bg={s.bg} fg={s.fg} size={28} /><span style={{ fontSize: 15, fontWeight: 700, color: T2.ink, width: 23 }}>{s.value}</span><span style={{ flex: 1, fontSize: 12.5, color: T2.ink }}>{s.label}</span><ChevronRight size={14} color={T2.ink3} /></button>)}
              </Card>
              <Card style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}><div style={{ fontSize: 14, fontWeight: 700, color: T2.ink }}>Needs your attention</div><LinkBtn onClick={() => navigate('/mission-control')}>View all</LinkBtn></div>
                {attentionItems.length === 0 ? <div style={{ fontSize: 13, color: T2.ink3, padding: '8px 0' }}>You're all caught up.</div> : attentionItems.slice(0, 2).map((it, i) => <button key={it.id} onClick={() => navigate(it.link)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', border: 'none', borderTop: i ? `1px solid ${T2.border}` : 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}><Bell size={14} color="var(--lyra-color-danger)" /><span style={{ fontSize: 12.5, color: T2.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.title}</span></button>)}
              </Card>
              <Card style={{ padding: '14px 16px' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T2.ink, marginBottom: 6 }}>Quick actions</div>
                {quick.map((q, i) => <button key={q.label} onClick={() => goChat(q.prompt, true)} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 1px', border: 'none', borderTop: i ? `1px solid ${T2.border}` : 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 12.5, color: T2.ink }}><q.Icon size={14} color={T2.ink2} /> {q.label}</button>)}
              </Card>
            </div>

            {/* Your work + Today's schedule */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : 'minmax(0,1.15fr) minmax(0,1fr)', gap: 20 }}>
              <Card style={{ padding: '22px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: T2.ink }}>Your work</div>
                  <LinkBtn onClick={() => navigate('/mission-control')}>View all</LinkBtn>
                </div>
                <div style={{ display: 'flex', gap: 6, borderBottom: `1px solid ${T2.border}`, marginBottom: 14 }}>
                  {([['running', `Running (${activeRuns.length})`], ['completed', 'Completed'], ['queued', 'Queued']] as const).map(([id, label]) => (
                    <button key={id} onClick={() => setWorkTab(id)} style={{ padding: '10px 14px', border: 'none', background: 'none', fontSize: 13.5, fontWeight: 600, color: workTab === id ? T2.accentText : T2.ink3, borderBottom: `2.5px solid ${workTab === id ? T2.accentText : 'transparent'}`, marginBottom: -1, cursor: 'pointer' }}>{label}</button>
                  ))}
                </div>
                {workRows[workTab].length === 0 ? (
                  <div style={{ fontSize: 13, color: T2.ink3, padding: '10px 2px' }}>
                    {emptyText[workTab]}{' '}
                    {workTab === 'running' && <LinkBtn onClick={() => navigate('/agents')}>Run an agent</LinkBtn>}
                    {workTab === 'queued' && <LinkBtn onClick={() => navigate('/schedule')}>Schedule something</LinkBtn>}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {workRows[workTab].map((r) => (
                      <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <IconTile Icon={r.Icon} bg={r.bg} fg={r.fg} size={50} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: T2.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                          <div style={{ fontSize: 13, color: T2.ink3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>{r.sub}</div>
                          {r.status === 'running' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                              <div style={{ flex: 1, height: 6, borderRadius: 999, background: 'var(--app-surface-3)', overflow: 'hidden' }}>
                                <div className="hv2-indeterminate" style={{ height: '100%', width: '40%', borderRadius: 999, background: 'var(--lyra-color-success)' }} />
                              </div>
                              <span style={{ fontSize: 12.5, color: T2.ink3 }}>{r.right}</span>
                            </div>
                          )}
                        </div>
                        {r.status !== 'running' && r.right && <span style={{ fontSize: 12.5, color: T2.ink3, flexShrink: 0 }}>{r.right}</span>}
                        <span style={pillStyle(r.status)}>
                          {r.status === 'running' ? <Zap size={12} /> : r.status === 'completed' ? <CheckCircle2 size={12} /> : <CalendarClock size={12} />}
                          {r.status === 'running' ? 'Running' : r.status === 'completed' ? 'Done' : 'Queued'}
                        </span>
                        <button onClick={() => navigate('/mission-control')} title="Open in Mission Control" style={{ border: 'none', background: 'none', cursor: 'pointer', color: T2.ink, display: 'flex', padding: 4 }}><MoreVertical size={17} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card style={{ padding: '22px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: T2.ink }}>Today's schedule</div>
                  <LinkBtn onClick={() => navigate('/schedule')}>View calendar</LinkBtn>
                </div>
                {todayScheduleItems.length === 0 ? (
                  <div style={{ fontSize: 13, color: T2.ink3 }}>Nothing scheduled today. <LinkBtn onClick={() => navigate('/schedule')}>Schedule something</LinkBtn></div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <div style={{ position: 'absolute', left: 100, top: 8, bottom: 8, width: 1, background: T2.border }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                      {todayScheduleItems.slice(0, 5).map((it) => {
                        const dot = it.type === 'workflow' ? 'var(--lyra-color-primary)' : it.type === 'task' ? 'var(--lyra-color-primary)' : 'var(--lyra-color-success)';
                        const typeLabel = it.type === 'workflow' ? 'Workflow' : it.type === 'task' ? 'Task' : 'Agent';
                        const done = it.type === 'task' && it.status === 'Done';
                        return (
                          <div key={`${it.type}-${it.id}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
                            <div style={{ width: 88, fontSize: 13, color: T2.ink3, flexShrink: 0, paddingTop: 1 }}>{it.time ? timeOf(it.time) : 'Due today'}</div>
                            <div style={{ width: 25, display: 'flex', justifyContent: 'center', paddingTop: 5, zIndex: 1 }}>
                              <span style={{ width: 11, height: 11, borderRadius: '50%', background: dot, boxShadow: '0 0 0 3px var(--lyra-color-surface)' }} />
                            </div>
                            <div style={{ minWidth: 0, paddingLeft: 12 }}>
                              <div style={{ fontSize: 14, fontWeight: 600, color: done ? T2.ink3 : T2.ink, textDecoration: done ? 'line-through' : 'none' }}>{it.name}</div>
                              <div style={{ fontSize: 13, color: T2.ink3, marginTop: 2 }}>{typeLabel}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {/* Insight */}
            {insight && !insightDismissed && (
              <Card style={{ padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <IconTile Icon={Lightbulb} bg="var(--app-surface-3)" fg="var(--lyra-color-success)" size={38} />
                <div style={{ fontSize: 13.5, fontWeight: 700, color: T2.ink }}>Insight for you</div>
                <div style={{ flex: 1, minWidth: 200, fontSize: 14, color: T2.ink2 }}>{insight.text}</div>
                <button onClick={() => navigate(insight.link)} style={{ height: 38, padding: '0 16px', borderRadius: 10, border: `1px solid ${T2.border}`, background: 'var(--lyra-color-surface)', fontSize: 13.5, fontWeight: 600, color: T2.ink, cursor: 'pointer' }}>View details →</button>
                <button onClick={() => setInsightDismissed(true)} aria-label="Dismiss insight" style={{ border: 'none', background: 'none', cursor: 'pointer', color: T2.ink, display: 'flex', padding: 4 }}><X size={18} /></button>
              </Card>
            )}
          </div>

          {/* ════ Right rail ════ */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            <HomeAiTeamPanel
              domains={capabilityDomains}
              selection={selection}
              connectors={offered}
              externalCount={externalAgents.length}
              onAddCustomAgent={() => setCapabilitiesOpen(true)}
              onConnect={startConnection}
              onDisconnect={disconnectConnection}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes hv2-slide { 0% { transform: translateX(-100%); } 100% { transform: translateX(250%); } }
        .hv2-indeterminate { animation: hv2-slide 1.4s ease-in-out infinite; }
      `}</style>

      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} isDarkMode={false} />
      {workforceOpen && (
        <div role="dialog" aria-label="Connect Workforce" onClick={(e) => { if (e.target === e.currentTarget) setWorkforceOpen(false); }}
          onKeyDown={(e) => { if (e.key === 'Escape') setWorkforceOpen(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(8,12,24,.55)', zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ width: 'min(560px, 100%)', maxHeight: '88vh', overflowY: 'auto', background: 'var(--lyra-color-surface)', border: '0.5px solid var(--app-border-strong)', borderRadius: 18, padding: 22, boxShadow: '0 24px 60px rgba(8,12,24,.35)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
              <div><div style={{ fontSize: 18, fontWeight: 700, color: T2.ink }}>Connect Workforce</div><div style={{ fontSize: 13, color: T2.ink3, marginTop: 4, lineHeight: 1.5 }}>Choose an organization, or continue to Workforce to authorize access.</div></div>
              <button onClick={() => setWorkforceOpen(false)} aria-label="Close" autoFocus style={{ flexShrink: 0, width: 32, height: 32, border: `1px solid ${T2.border}`, background: 'var(--lyra-color-surface-2)', color: T2.ink, borderRadius: 10, cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>✕</button>
            </div>
            <WorkforceConnect
              agent={selection.entries.find((entry) => entry.agent.domain === 'hr')?.agent || null}
              selected={(() => { const agent = selection.entries.find((entry) => entry.agent.domain === 'hr')?.agent; return agent ? selection.isSelected(agent.id) : false; })()}
              onToggleSelect={() => { const agent = selection.entries.find((entry) => entry.agent.domain === 'hr')?.agent; if (agent) selection.toggle(agent.id); }}
              onConnectionChange={() => { loadOffered(); selection.reload(); }}
            />
          </div>
        </div>
      )}
      {capabilitiesOpen && (
        <div role="dialog" aria-label="Custom agents" onClick={(e) => { if (e.target === e.currentTarget) setCapabilitiesOpen(false); }}
          onKeyDown={(e) => { if (e.key === 'Escape') setCapabilitiesOpen(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(8,12,24,.55)', zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ width: 'min(660px, 100%)', maxHeight: '88vh', overflowY: 'auto', background: 'var(--lyra-color-surface)', border: '0.5px solid var(--app-border-strong)', borderRadius: 18, padding: 22, boxShadow: '0 24px 60px rgba(8,12,24,.35)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: T2.ink, letterSpacing: -0.2 }}>Custom agents</div>
                <div style={{ fontSize: 13, color: 'var(--lyra-color-text-muted)', marginTop: 4, lineHeight: 1.5 }}>Add a specialized external agent when your workspace capabilities are not enough.</div>
              </div>
              <button onClick={() => setCapabilitiesOpen(false)} aria-label="Close" autoFocus style={{ flexShrink: 0, width: 32, height: 32, border: '0.5px solid var(--lyra-color-border)', background: 'var(--lyra-color-surface-2)', color: T2.ink, borderRadius: 10, cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>✕</button>
            </div>
            <ExternalAgentsPanel sidePanel selectedIds={selectedExternalAgentIds} onToggleInChat={(id) => setSelectedExternalAgentIds((ids) => ids.includes(id) ? ids.filter((selected) => selected !== id) : [...ids, id])} />
          </div>
        </div>
      )}
</AppShellV2>
  );
}
